// src/components/CaseDetailsPage.js
// Full-page "War Room" dashboard for case management

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'animate.css';

const CaseDetailsPage = ({
  authAxios,
  cases,
  clients,
  users,
  serviceProducts,
  fetchCases,
  showFormMessage,
  caseTypeOptions,
  caseStatusOptions,
  caseStageOptions
}) => {
  const { id: caseId, tab: initialTab } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState({
    documents: [],
    timeEntries: [],
    expenseEntries: [],
    invoices: [],
    tasks: [],
    communicationLogs: [],
    events: []
  });

  // Fetch case details
  const fetchCaseDetails = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const resp = await authAxios.get(`/cases/${caseId}/`);
      setCaseData(resp.data);
    } catch (err) {
      console.error('Failed to fetch case details', err);
      showFormMessage?.('Failed to load case details', 'error');
    } finally {
      setLoading(false);
    }
  }, [authAxios, caseId, showFormMessage]);

  // Fetch all related records
  const fetchRecords = useCallback(async () => {
    if (!caseId) return;
    try {
      const [docs, time, expense, inv, tasks, comms, events] = await Promise.all([
        authAxios.get('/documents/', { params: { case: caseId } }),
        authAxios.get('/time-entries/', { params: { case: caseId } }),
        authAxios.get('/expense-entries/', { params: { case: caseId } }),
        authAxios.get('/invoices/', { params: { case: caseId } }),
        authAxios.get('/tasks/', { params: { case: caseId } }),
        authAxios.get('/communication-logs/', { params: { case: caseId } }),
        authAxios.get('/events/', { params: { case: caseId } }).catch(() => ({ data: { results: [] } }))
      ]);

      setRecords({
        documents: docs.data?.results || docs.data || [],
        timeEntries: time.data?.results || time.data || [],
        expenseEntries: expense.data?.results || expense.data || [],
        invoices: inv.data?.results || inv.data || [],
        tasks: tasks.data?.results || tasks.data || [],
        communicationLogs: comms.data?.results || comms.data || [],
        events: events.data?.results || events.data || []
      });
    } catch (err) {
      console.error('Failed to fetch case records', err);
    }
  }, [authAxios, caseId]);

  useEffect(() => {
    fetchCaseDetails();
    fetchRecords();
  }, [fetchCaseDetails, fetchRecords, caseId]);

  // Calculate case statistics
  const stats = useMemo(() => {
    if (!caseData) return {};

    const totalHoursBilled = records.timeEntries
      .filter(t => t.is_billable && t.invoice)
      .reduce((sum, t) => sum + (parseFloat(t.duration_hours) || 0), 0);

    const totalHoursUnbilled = records.timeEntries
      .filter(t => t.is_billable && !t.invoice)
      .reduce((sum, t) => sum + (parseFloat(t.duration_hours) || 0), 0);

    const totalRevenue = records.invoices
      .filter(inv => ['Sent', 'Paid', 'Overdue'].includes(inv.status))
      .reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);

    const totalExpenses = records.expenseEntries
      .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

    const openTasks = records.tasks.filter(t => t.status !== 'Completed').length;
    const completedTasks = records.tasks.filter(t => t.status === 'Completed').length;

    const nextCourtDate = records.events
      .filter(e => e.event_type === 'Court Hearing' && new Date(e.start_time) > new Date())
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];

    return {
      totalHoursBilled,
      totalHoursUnbilled,
      totalRevenue,
      totalExpenses,
      openTasks,
      completedTasks,
      nextCourtDate
    };
  }, [caseData, records]);

  // Activity timeline - combine all activities
  const activityTimeline = useMemo(() => {
    const activities = [];

    records.documents.forEach(doc => {
      activities.push({
        type: 'document',
        icon: 'bi-file-earmark',
        color: 'blue',
        title: `Document uploaded: ${doc.title}`,
        timestamp: new Date(doc.uploaded_at),
        data: doc
      });
    });

    records.timeEntries.forEach(te => {
      activities.push({
        type: 'time',
        icon: 'bi-clock',
        color: 'green',
        title: `${te.duration_hours}h logged by ${te.user_name || 'Unknown'}`,
        description: te.description,
        timestamp: new Date(te.date),
        data: te
      });
    });

    records.tasks.forEach(task => {
      activities.push({
        type: 'task',
        icon: task.status === 'Completed' ? 'bi-check-circle' : 'bi-circle',
        color: task.status === 'Completed' ? 'green' : 'yellow',
        title: `Task: ${task.title}`,
        description: `Status: ${task.status}`,
        timestamp: new Date(task.created_at),
        data: task
      });
    });

    records.communicationLogs.forEach(comm => {
      activities.push({
        type: 'communication',
        icon: 'bi-chat-dots',
        color: 'purple',
        title: `${comm.communication_type || 'Communication'} with ${comm.contact_name || 'Unknown'}`,
        description: comm.summary,
        timestamp: new Date(comm.date),
        data: comm
      });
    });

    records.invoices.forEach(inv => {
      activities.push({
        type: 'invoice',
        icon: 'bi-receipt',
        color: 'indigo',
        title: `Invoice ${inv.invoice_number || inv.id} - ${inv.status}`,
        description: `Amount: P${parseFloat(inv.total_amount || 0).toFixed(2)}`,
        timestamp: new Date(inv.issue_date),
        data: inv
      });
    });

    // Sort by timestamp desc
    return activities.sort((a, b) => b.timestamp - a.timestamp);
  }, [records]);

  const getClient = useCallback(() => {
    if (!caseData || !clients) return null;
    return clients.find(c => c.id === caseData.client);
  }, [caseData, clients]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <div className="mt-4 text-gray-600">Loading case details...</div>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center text-gray-500">
          <i className="bi bi-exclamation-triangle text-6xl mb-4"></i>
          <div className="text-xl">Case not found</div>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const client = getClient();
  const clientName = client
    ? client.client_type === 'individual'
      ? `${client.first_name} ${client.last_name}`
      : client.organization_name
    : 'Unknown Client';

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        {/* Top bar */}
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-800"
            >
              <i className="bi bi-arrow-left text-xl"></i>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{caseData.title}</h1>
              <div className="text-sm text-gray-600 mt-1">
                {clientName} • {caseData.case_type} • {caseData.status}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                caseData.status === 'Open'
                  ? 'bg-green-100 text-green-700'
                  : caseData.status === 'Closed'
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-yellow-100 text-yellow-700'
              }`}
            >
              {caseData.status}
            </span>
            <button className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
              <i className="bi bi-pencil mr-2"></i>
              Edit Case
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Billed Hours</div>
              <div className="text-xl font-bold text-gray-800">{stats.totalHoursBilled?.toFixed(1) || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Unbilled Hours</div>
              <div className="text-xl font-bold text-yellow-600">{stats.totalHoursUnbilled?.toFixed(1) || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Revenue</div>
              <div className="text-xl font-bold text-green-600">P{stats.totalRevenue?.toFixed(0) || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Expenses</div>
              <div className="text-xl font-bold text-red-600">P{stats.totalExpenses?.toFixed(0) || 0}</div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Tasks</div>
              <div className="text-xl font-bold text-gray-800">
                {stats.openTasks || 0} / {(stats.openTasks || 0) + (stats.completedTasks || 0)}
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 uppercase">Next Court Date</div>
              <div className="text-sm font-bold text-blue-600">
                {stats.nextCourtDate ? new Date(stats.nextCourtDate.start_time).toLocaleDateString() : 'None'}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex gap-1 border-b border-gray-200">
            {[
              { id: 'overview', label: 'Overview', icon: 'bi-grid' },
              { id: 'tasks', label: 'Tasks', icon: 'bi-check-circle', badge: stats.openTasks },
              { id: 'documents', label: 'Documents', icon: 'bi-folder', badge: records.documents.length },
              { id: 'billing', label: 'Billing', icon: 'bi-receipt', badge: records.invoices.length },
              { id: 'communications', label: 'Communications', icon: 'bi-chat', badge: records.communicationLogs.length },
              { id: 'timeline', label: 'Timeline', icon: 'bi-clock-history' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  navigate(`/cases/${caseId}/${tab.id}`);
                }}
                className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
                {tab.badge > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Case Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Case Information</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Client</div>
                  <div className="font-medium">{clientName}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Case Type</div>
                  <div className="font-medium">{caseData.case_type}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Stage</div>
                  <div className="font-medium">{caseData.case_stage}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">Opened</div>
                  <div className="font-medium">{new Date(caseData.date_opened).toLocaleDateString()}</div>
                </div>
                {caseData.description && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Description</div>
                    <div className="text-sm text-gray-700">{caseData.description}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Team */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold mb-4">Team</h3>
              <div className="space-y-2">
                {caseData.partner_name && (
                  <div className="flex items-center gap-2">
                    <i className="bi bi-person-badge text-blue-600"></i>
                    <span className="font-medium">Partner:</span>
                    <span>{caseData.partner_name}</span>
                  </div>
                )}
                {caseData.associate_names && caseData.associate_names.length > 0 && (
                  <div className="flex items-center gap-2">
                    <i className="bi bi-people text-green-600"></i>
                    <span className="font-medium">Associates:</span>
                    <span>{caseData.associate_names.join(', ')}</span>
                  </div>
                )}
                {caseData.paralegal_names && caseData.paralegal_names.length > 0 && (
                  <div className="flex items-center gap-2">
                    <i className="bi bi-person text-purple-600"></i>
                    <span className="font-medium">Paralegals:</span>
                    <span>{caseData.paralegal_names.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {activityTimeline.slice(0, 10).map((activity, idx) => (
                  <div key={idx} className="flex gap-3 pb-3 border-b border-gray-100 last:border-0">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-${activity.color}-100 flex items-center justify-center`}>
                      <i className={`${activity.icon} text-${activity.color}-600`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{activity.title}</div>
                      {activity.description && (
                        <div className="text-sm text-gray-600 truncate">{activity.description}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {activity.timestamp.toLocaleDateString()} {activity.timestamp.toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Tasks</h3>
              <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                <i className="bi bi-plus-lg mr-2"></i>
                New Task
              </button>
            </div>
            <div className="space-y-2">
              {records.tasks.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No tasks yet</div>
              ) : (
                records.tasks.map(task => (
                  <div
                    key={task.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-medium">{task.title}</div>
                        {task.description && (
                          <div className="text-sm text-gray-600 mt-1">{task.description}</div>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Due: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}</span>
                          <span>Assigned: {task.assigned_to_name || 'Unassigned'}</span>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            task.status === 'Completed'
                              ? 'bg-green-100 text-green-700'
                              : task.status === 'In Progress'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {task.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Documents</h3>
            <div className="text-center text-gray-500 py-8">
              Document management integrated with DocumentExplorer
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Billing & Invoices</h3>
            <div className="space-y-4">
              {/* Time Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-600">Billed Hours</div>
                  <div className="text-2xl font-bold text-blue-700">{stats.totalHoursBilled?.toFixed(1)}</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="text-sm text-gray-600">Unbilled Hours</div>
                  <div className="text-2xl font-bold text-yellow-700">{stats.totalHoursUnbilled?.toFixed(1)}</div>
                </div>
              </div>

              {/* Invoices */}
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Invoices</h4>
                {records.invoices.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">No invoices yet</div>
                ) : (
                  <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium">Invoice #</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Amount</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.invoices.map(inv => (
                        <tr key={inv.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-2 text-sm">{inv.invoice_number || inv.id}</td>
                          <td className="px-4 py-2 text-sm">{new Date(inv.issue_date).toLocaleDateString()}</td>
                          <td className="px-4 py-2 text-sm font-medium">P{parseFloat(inv.total_amount || 0).toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${
                                inv.status === 'Paid'
                                  ? 'bg-green-100 text-green-700'
                                  : inv.status === 'Overdue'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {inv.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'communications' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Communications</h3>
            <div className="space-y-3">
              {records.communicationLogs.length === 0 ? (
                <div className="text-center text-gray-500 py-8">No communications yet</div>
              ) : (
                records.communicationLogs.map(comm => (
                  <div key={comm.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium">{comm.communication_type || 'Communication'}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          {comm.contact_name && `With: ${comm.contact_name}`}
                        </div>
                        {comm.summary && (
                          <div className="text-sm text-gray-700 mt-2">{comm.summary}</div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(comm.date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
            <div className="space-y-4">
              {activityTimeline.map((activity, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full bg-${activity.color}-100 flex items-center justify-center`}>
                      <i className={`${activity.icon} text-${activity.color}-600`}></i>
                    </div>
                    {idx < activityTimeline.length - 1 && (
                      <div className="w-0.5 flex-1 bg-gray-200 my-2"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="font-medium">{activity.title}</div>
                    {activity.description && (
                      <div className="text-sm text-gray-600 mt-1">{activity.description}</div>
                    )}
                    <div className="text-xs text-gray-400 mt-2">
                      {activity.timestamp.toLocaleDateString()} at {activity.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseDetailsPage;
